import React, { useState, useEffect, useRef } from 'react';
import { Heart, Star, Users, Award, CheckCircle, Clock, Target, Trophy, Crown, Shield } from 'lucide-react';

const AboutSection = () => {
  const [inView, setInView] = useState(false);
  const [hoveredAdvantage, setHoveredAdvantage] = useState(null);
  const [statsAnimated, setStatsAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          setTimeout(() => setStatsAnimated(true), 500);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const advantages = [
    {
      icon: Crown,
      title: "Pasiune pentru Perfecțiune",
      description: "Fiecare trusou este confecționat cu dragoste și atenție la detalii pentru ziua specială a copilului.",
      gradient: "from-navy-500 to-navy-700"
    },
    {
      icon: Trophy,
      title: "Experiență Dovedită",
      description: "Trusouri de botez confecționate cu măiestrie și feedback pozitiv de la familii mulțumite.",
      gradient: "from-gold-500 to-gold-700"
    },
    {
      icon: Users,
      title: "Echipă de Croitori Profesioniști",
      description: "Specialiști în confecționarea textilelor cu ani de experiență în croitorie și broderie.",
      gradient: "from-emerald-500 to-emerald-700"
    },
    {
      icon: Award,
      title: "Materiale Premium",
      description: "Folosim doar materiale de cea mai înaltă calitate: mătase, bumbac fin și broderii elegante.",
      gradient: "from-navy-600 to-emerald-600"
    },
    {
      icon: Target,
      title: "Confecție Personalizată",
      description: "Fiecare trusou este adaptat cerințelor dumneavoastră cu măsuri precise și finisaje impecabile.",
      gradient: "from-emerald-600 to-gold-600"
    },
    {
      icon: Shield,
      title: "Respectarea Termenelor",
      description: "Ne mândrim cu respectarea strictă a termenelor de livrare stabilite cu fiecare client.",
      gradient: "from-gold-600 to-navy-600"
    }
  ];

  const features = [
    { text: "Planificare detaliată și personalizată", icon: CheckCircle },
    { text: "Colaboratori și furnizori de încredere", icon: Star },
    { text: "Atenție la detalii și calitate superioară", icon: Heart },
    { text: "Suport complet pe toată durata organizării", icon: Users },
    { text: "Prețuri transparente, fără costuri ascunse", icon: Target },
    { text: "Flexibilitate și adaptare la preferințe", icon: Trophy }
  ];

  const stats = [
    { number: 150, label: "Botezuri organizate", suffix: "+", icon: Crown },
    { number: 50, label: "Furnizori parteneri", suffix: "+", icon: Users },
    { number: 98, label: "Clienți mulțumiți", suffix: "%", icon: Heart },
    { number: 5, label: "Rating mediu", suffix: "★", icon: Star }
  ];

  const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!statsAnimated) return;

      let startTime = null;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }, [end, duration, statsAnimated]);

    return <>{count}{suffix}</>;
  };

  return (
    <section id="despre" ref={sectionRef} className="py-20 cream-pattern relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-32 -left-32 w-96 h-96 rounded-full bg-gradient-to-r from-navy-100/10 to-gold-100/10 blur-3xl"></div>
          <div className="absolute bottom-32 -right-32 w-128 h-128 rounded-full bg-gradient-to-r from-emerald-100/10 to-navy-100/10 blur-3xl"></div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center px-8 py-3 rounded-full glass border border-navy-200/50 text-navy-600 font-medium text-sm mb-6">
            <Crown className="w-4 h-4 mr-2 text-gold-600 animate-pulse" />
            Despre Echipa Noastră
          </div>
          
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 font-display">
            Despre <span className="gradient-text">BOTEZ DE POVESTE</span>
          </h3>
          
          <div className="w-32 h-1 bg-gradient-to-r from-navy-500 to-gold-500 mx-auto mb-8 rounded-full"></div>
          
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Specializați în crearea de experiențe de neuitat pentru 
            <span className="gradient-text font-semibold"> ziua specială </span>
            a copilului dumneavoastră
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Left Column - Text Content */}
          <div className={`transition-all duration-1000 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="relative">
              <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 font-display">
                Experiență și <span className="gradient-text">Profesionalism</span> în Fiecare Detaliu
              </h4>
              
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p className="text-lg">
                  <span className="gradient-text font-semibold">BOTEZ DE POVESTE</span> este specializată în organizarea de botezuri memorabile, 
                  aducând în fiecare eveniment creativitate, atenție la detalii și o abordare 
                  personalizată pentru fiecare familie.
                </p>
                
                <p className="text-lg">
                  Înțelegem importanța acestei zile speciale în viața copilului și a familiei 
                  dumneavoastră, de aceea ne dedicăm întru totul pentru a crea o experiență 
                  de neuitat, plină de bucurie și amintiri frumoase care să dureze o viață întreagă.
                </p>
              </div>
              
              {/* Features List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
                {features.map((feature, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center p-4 rounded-xl bg-white/80 border border-navy-100/50 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300 group ${
                      inView ? 'fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-navy-500 to-emerald-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 font-medium group-hover:text-navy-600 transition-colors duration-300">
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Advantages Cards */}
          <div className={`transition-all duration-1000 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-navy-100/30 to-gold-100/30 rounded-3xl blur-xl"></div>
              <div className="relative glass rounded-3xl p-8 border border-white/20 shadow-lg">
                <h4 className="text-2xl font-bold text-navy-800 mb-8 text-center font-display">
                  De Ce Să Ne Alegeți?
                </h4>
                
                <div className="grid grid-cols-1 gap-6">
                  {advantages.map((advantage, index) => (
                    <div 
                      key={index} 
                      className={`group cursor-pointer transition-all duration-500 ${
                        inView ? 'fade-in-scale' : 'opacity-0'
                      }`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                      onMouseEnter={() => setHoveredAdvantage(index)}
                      onMouseLeave={() => setHoveredAdvantage(null)}
                    >
                      <div className={`p-6 rounded-2xl transition-all duration-300 ${
                        hoveredAdvantage === index 
                          ? 'glass-navy scale-105 shadow-xl' 
                          : 'glass hover:scale-105'
                      }`}>
                        <div className="flex items-start">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${advantage.gradient} flex items-center justify-center mr-4 flex-shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                            <advantage.icon className="w-6 h-6 text-white" />
                          </div>
                          
                          <div className="flex-1">
                            <h5 className={`font-bold mb-2 transition-colors duration-300 ${
                              hoveredAdvantage === index ? 'text-white' : 'text-navy-800 group-hover:text-navy-600'
                            }`}>
                              {advantage.title}
                            </h5>
                            <p className={`text-sm leading-relaxed transition-colors duration-300 ${
                              hoveredAdvantage === index ? 'text-white/90' : 'text-gray-600 group-hover:text-gray-700'
                            }`}>
                              {advantage.description}
                            </p>
                          </div>
                        </div>
                        
                        {/* Hover Effect Line */}
                        <div className={`h-0.5 bg-gradient-to-r ${advantage.gradient} mt-4 rounded-full transition-all duration-300 ${
                          hoveredAdvantage === index ? 'w-full opacity-100' : 'w-0 opacity-0'
                        }`}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className={`transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '1s' }}>
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-navy-500/5 via-gold-500/5 to-emerald-500/5 rounded-3xl blur-xl"></div>
            <div className="relative modern-card rounded-3xl p-8 shadow-2xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {stats.map((stat, index) => (
                  <div 
                    key={index} 
                    className={`text-center group cursor-pointer transition-all duration-500 ${
                      statsAnimated ? 'fade-in-scale' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <div className="relative mb-4">
                      <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg ${
                        index === 0 ? 'bg-gradient-to-r from-navy-500 to-navy-700' :
                        index === 1 ? 'bg-gradient-to-r from-emerald-500 to-emerald-700' :
                        index === 2 ? 'bg-gradient-to-r from-gold-500 to-gold-700' :
                        'bg-gradient-to-r from-gold-600 to-navy-600'
                      }`}>
                        <stat.icon className="w-8 h-8 text-white" />
                      </div>
                      
                      {/* Floating particles on hover */}
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-gold-400 rounded-full opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300"></div>
                    </div>
                    
                    <div className="text-4xl font-bold text-gray-900 mb-2 group-hover:text-navy-600 transition-colors duration-300">
                      <AnimatedCounter end={stat.number} suffix={stat.suffix} />
                    </div>
                    
                    <div className="text-gray-600 text-sm font-medium group-hover:text-gray-700 transition-colors duration-300">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Decorations */}
      <div className="absolute top-20 left-8 w-4 h-4 bg-navy-300 rounded-full opacity-40 particle-1"></div>
      <div className="absolute top-48 right-12 w-3 h-3 bg-gold-400 rounded-full opacity-30 particle-2"></div>
      <div className="absolute bottom-32 left-16 w-2 h-2 bg-emerald-300 rounded-full opacity-50 particle-1"></div>
      <div className="absolute bottom-48 right-8 w-5 h-5 bg-navy-300 rounded-full opacity-20 particle-2"></div>
    </section>
  );
};

export default AboutSection;